// Link da equipe que abre a tela sozinho (213 v2 · 02/10/2026).
// A pessoa toca no link /equipe/<token>: o servidor (Edge `equipe-entrar`) cria a "conta de equipe" daquele
// convite e devolve um código de uso único; o celular entra com ele e aceita o convite com a função de
// sempre (equipe_aceitar_convite). Conta de equipe = app_metadata.equipe === true (quem põe é o servidor).
import { supabase } from '@/lib/supabase';

export type AberturaLink =
  | { tipo: 'abrir' } // pronto: ir para /servir
  | { tipo: 'pessoal'; mensagem?: string } // conta pessoal neste celular, ou convite aceito com conta pessoal
  | { tipo: 'erro'; motivo?: string; mensagem: string };

export interface TelaEquipe {
  tela: 'caixa' | 'barraca' | 'portaria';
  rotulo: string;
  url: string;
  ponto_id?: number;
  caixa_id?: number; // caixa escolhido pela direção (abre direto nele, com a maquininha dele)
}
export interface MinhasTelas {
  equipe: boolean;
  nome?: string;
  evento_id?: number;
  evento?: string;
  encerrado?: boolean;
  telas?: TelaEquipe[];
}

type ComMetadados = { app_metadata?: Record<string, unknown> | null } | null | undefined;
export function ehContaDeEquipe(user: ComMetadados): boolean {
  return user?.app_metadata?.equipe === true;
}

export async function abrirPeloLink(token: string, opcoes: { usarEsteCelular?: boolean } = {}): Promise<AberturaLink> {
  const t = (token || '').trim();
  const { data: s } = await supabase.auth.getSession();
  const user = s.session?.user ?? null;

  // 1 · este celular já está com a conta de equipe DESTE link: só confirma e abre
  if (ehContaDeEquipe(user)) {
    const { data: meu } = await supabase.rpc('equipe_link_e_meu', { p_token: t });
    if (meu === true) {
      const { data: a } = await supabase.rpc('equipe_aceitar_convite', { p_token: t });
      if (a?.ok) return { tipo: 'abrir' };
      return { tipo: 'erro', motivo: 'aceite', mensagem: a?.erro || 'Não consegui abrir a sua tela. Fale com a direção.' };
    }
    // é a conta de equipe de OUTRO link: troca por esta (segue abaixo)
  } else if (user && !opcoes.usarEsteCelular) {
    // conta pessoal neste celular: a tela pergunta o que a pessoa quer
    return { tipo: 'pessoal' };
  }

  // 2 · o servidor cria (uma vez) e abre a conta de equipe deste convite
  const { data: r, error } = await supabase.functions.invoke('equipe-entrar', { body: { token: t } });
  if (error || !r) {
    return { tipo: 'erro', motivo: 'rede', mensagem: 'Sem sinal agora. Toque de novo no link em alguns segundos.' };
  }
  if (!r.ok) {
    if (r.motivo === 'conta_pessoal') return { tipo: 'pessoal', mensagem: r.mensagem };
    return { tipo: 'erro', motivo: r.motivo, mensagem: r.mensagem || 'Não consegui abrir a sua tela.' };
  }

  // 3 · entra com o código de uso único (a sessão deste celular passa a ser a conta de equipe)
  let v = await supabase.auth.verifyOtp({ token_hash: r.token_hash, type: r.tipo || 'magiclink' });
  if (v.error) v = await supabase.auth.verifyOtp({ token_hash: r.token_hash, type: 'email' });
  if (v.error || !v.data?.session) {
    return { tipo: 'erro', motivo: 'sessao', mensagem: 'Não consegui abrir a sua tela. Toque de novo no link.' };
  }

  // 4 · aceita o convite (a mesma função de sempre, do 203)
  const { data: a, error: ea } = await supabase.rpc('equipe_aceitar_convite', { p_token: t });
  if (ea || !a?.ok) {
    return { tipo: 'erro', motivo: 'aceite', mensagem: a?.erro || 'Não consegui ligar a sua função. Toque de novo no link.' };
  }
  return { tipo: 'abrir' };
}

const CHAVE_TELAS = 'modo_equipe_telas';

// Sai do modo equipe SÓ neste celular: encerra a sessão da conta de equipe
// (scope local — NUNCA global, porque a conta do link é compartilhada por
// vários celulares da equipe) e apaga o que este aparelho guardou do modo
// equipe. Depois disso a pessoa entra com a própria conta pelo fluxo normal.
export async function sairDoModoEquipeNesteCelular(): Promise<void> {
  try {
    localStorage.removeItem(CHAVE_TELAS);
  } catch {
    /* armazenamento indisponível */
  }
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    /* se falhar, a sessão local já foi abandonada mesmo assim */
  }
}

// As telas desta conta de equipe. Sem sinal: usa a última lista guardada neste celular.
export async function carregarMinhasTelas(): Promise<MinhasTelas> {
  const { data, error } = await supabase.rpc('equipe_minhas_telas');
  if (!error && data) {
    try {
      localStorage.setItem(CHAVE_TELAS, JSON.stringify(data));
    } catch {
      /* armazenamento indisponível */
    }
    return data as MinhasTelas;
  }
  try {
    const guardado = localStorage.getItem(CHAVE_TELAS);
    if (guardado) return JSON.parse(guardado) as MinhasTelas;
  } catch {
    /* ignora */
  }
  return { equipe: true, telas: [] };
}

// Esta rota pode abrir no modo equipe?
export function rotaPermitidaNoModoEquipe(pathname: string, dados: MinhasTelas | null): boolean {
  if (pathname === '/servir' || pathname.startsWith('/equipe/')) return true;
  if (!dados || dados.encerrado) return false;
  return (dados.telas ?? []).some((t) => {
    const base = t.url.split('?')[0];
    return pathname === base || pathname.startsWith(base + '/');
  });
}